// MessageHandler.ts
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { inject, Injectable } from '@angular/core';
import {
  USER_DOWNLOAD_ACTIONS,
  USER_EXPORT_ACTIONS,
} from '@data-access/downloads-exports';
import { Thread } from '@data-access/submissions';
import * as DocumentActions from '@documents/actions/documents.actions';
import { Store } from '@ngrx/store';
import { UtilsService } from '@shared/services/utils.service';
import { selectUser } from '@shared/store/selectors/user.selectors';
import {
  selectReviewerDocumentById,
  selectSubmission,
  selectThread,
} from '@submissions/v1/redux/submission.selectors';
import { OutcomesService } from '@submissions/v1/services/outcomes.service';
import { cloneDeep } from 'lodash';
import { combineLatest, filter, map, take, tap } from 'rxjs';
import { User } from '@iris-sites/interfaces/sites.interfaces';
import * as QuestionnaireActions from '@submissions/v1/redux/questionnaire.actions';
import { WebSocketMessage } from '../models/message';
import * as UserActions from '@shared/store/actions/user.actions';
import * as SubmissionActions from '@submissions/v1/redux/submission.actions';
import { injectDispatch } from '@ngrx/signals/events';
import { TASK_DETAIL_EVENTS, TASK_EVENTS } from '@data-access/tasks/store';

@Injectable({
  providedIn: 'root',
})
export class MessageHandler {
  private readonly _utils = inject(UtilsService);
  private readonly _outcomesService = inject(OutcomesService);
  private readonly _store = inject(Store);
  private _taskEventDispatcher = injectDispatch(TASK_EVENTS);
  private _taskDetailEventDispatcher = injectDispatch(TASK_DETAIL_EVENTS);

  fixMessageData(message: WebSocketMessage) {
    try {
      if (Array.isArray(message.data) && message.data.length > 0) {
        message.data = this._utils.convertKeysToCamelCase(
          JSON.parse(message.data[0]),
        );
      } else if (typeof message.data === 'string') {
        message.data = this._utils.convertKeysToCamelCase(
          JSON.parse(message.data),
        );
      } else {
        message.data = this._utils.convertKeysToCamelCase(message.data);
      }
    } catch (error) {
      console.error('Error parsing JSON:', error);
      message.data = [];
    }
    return message;
  }

  handleReviewerDocumentMessage(message: WebSocketMessage) {
    switch (message.event) {
      case 'update_reviewer_document': {
        const document = JSON.parse(message.data);
        this._store.dispatch(
          SubmissionActions.setReviewMaterialDocuments({ document }),
        );
        break;
      }
      case 'delete_reviewer_document': {
        const documentId = message.entity_id;
        this._store.dispatch(
          SubmissionActions.deleteReviewMaterialDocuments({ documentId }),
        );
        break;
      }
      case 'add_reviewer_document': {
        this._store
          .select(selectReviewerDocumentById(message.entity_id))
          .subscribe((doc) => {
            if (!doc) {
              const document = JSON.parse(message.data);
              this._store.dispatch(
                SubmissionActions.updatingReviewerDocumentList({ document }),
              );
            }
          });
        break;
      }
      default:
        console.warn('Unknown reviewer document event:', message.event);
        break;
    }
  }

  handleDocumentMessage(message: WebSocketMessage) {
    const _validMessage = new Set<string>([
      'document scanned successfully. file is malicious',
    ]);
    switch (message.event) {
      case 'add_document': {
        let messageData = message.data;

        if (!messageData.document && !messageData.failed_documents) {
          messageData = {
            document: message.data,
            failed_documents: [],
          };
        }
        if (messageData && typeof messageData.document === 'string') {
          messageData.document = JSON.parse(messageData.document);
        }
        if (Array.isArray(messageData.failed_documents)) {
          messageData.failed_documents = messageData.failed_documents
            .filter((val: string) => !!val)
            .map((item: string) =>
              typeof item === 'string' ? JSON.parse(item) : item,
            );
        }

        const addData = this._utils.convertKeysToSnakeCase(messageData);
        let status: 'pending' | 'completed' | 'failed' = 'pending';
        const docStatus = addData.document['document_status']?.toLowerCase();
        if (['pending', 'completed', 'failed'].includes(docStatus))
          status = docStatus;

        this._store.dispatch(
          SubmissionActions.onDocumentUploadStatusChanged({
            document: addData['document'] ?? {},
            status,
            error: addData['document']['error'] ?? null,
          }),
        );

        const _reloadPage: boolean = coerceBooleanProperty(
          Object.prototype.hasOwnProperty.call(
            addData.document,
            'document_description',
          ) &&
            _validMessage.has(
              addData.document['document_description']?.toLowerCase(),
            ) &&
            Object.prototype.hasOwnProperty.call(
              addData.document,
              'document_status',
            ) &&
            addData.document['document_status']?.toLowerCase() === 'failed',
        );

        const _updateFailedDocuments = coerceBooleanProperty(
          Object.prototype.hasOwnProperty.call(addData, 'failed_documents') &&
            addData['failed_documents']?.length > 0 &&
            addData.document['document_status']?.toLowerCase() === 'failed',
        );
        if (_reloadPage) {
          this._store.dispatch(
            SubmissionActions.onDocumentUploadFailRefresh({
              message: addData.document['document_description'],
            }),
          );
        } else if (_updateFailedDocuments) {
          this._store.dispatch(
            SubmissionActions.onDocumentUploadFailedDocuments({
              failedDocuments: addData['failed_documents'],
            }),
          );
        } else {
          this._store.dispatch(
            DocumentActions.storeDocument({
              document: addData,
              workspaceId: message.workspace_id || '',
            }),
          );
        }
        break;
      }
      case 'delete_document': {
        const deleteData = this._utils.convertKeysToCamelCase(
          JSON.parse(message.data),
        );
        this._store.dispatch(
          DocumentActions.deleteDocumentSuccess({
            documentId: deleteData.id,
            entityId: deleteData.entityId,
            entityType: deleteData.entityType,
          }),
        );
        break;
      }
      case 'update_document': {
        const updateData = JSON.parse(message.data);
        this._store.dispatch(
          DocumentActions.updateDocumentSuccess({
            document: updateData,
            documentId: updateData.id,
          }),
        );
        break;
      }
      case 'create_message': {
        // create message for v1
        const body = this._utils.convertKeysToCamelCase(
          JSON.parse(message.data.message),
        );
        this._store.dispatch(
          SubmissionActions.addToThreadList({
            message: this._utils.convertKeysToCamelCase(
              JSON.parse(message.data.message),
            ),
          }),
        );
        this._store.dispatch(
          DocumentActions.updateThreadtoEntityDocument({
            entityId: message?.workspace_id ?? '',
            entityType: 'workspace',
            isResolved: false,
            threadId: message.entity_id,
          }),
        );
        this.updateOpenThreads(body);
        break;
      }
      case 'create_document_request': {
        this._store.dispatch(
          SubmissionActions.requestDocumentThreadCreationSuccess(),
        );
        // Reload task list and count
        this._reloadTaskListAndCount(message);
        break;
      }
      case 'reply': {
        const replyBody = this._utils.convertKeysToCamelCase(
          JSON.parse(message.data.message),
        );
        this._store.dispatch(
          SubmissionActions.replytoThread({
            message: replyBody,
          }),
        );
        this._store.dispatch(
          DocumentActions.updateThreadtoEntityDocument({
            entityId: message?.workspace_id ?? '',
            entityType: 'workspace',
            isResolved: replyBody.isResolved,
            threadId: message.entity_id,
          }),
        );
        break;
      }
      case 'respond_document_request': {
        const messageBody = this._utils.convertKeysToCamelCase(
          JSON.parse(message.data.message),
        );
        this._store.dispatch(
          SubmissionActions.respondDocumentRequestThread({
            message: messageBody,
          }),
        );
        break;
      }
      default:
        console.warn('Unknown document event:', message.event);
        break;
    }
  }
  handleQuestionnaireMessage(rawMessage: WebSocketMessage) {
    const message = this.fixMessageData(cloneDeep(rawMessage));

    switch (message.event) {
      case 'mark_answer': {
        if (message?.data?.length === 0) return;
        const answer = message.data;
        this._store.dispatch(QuestionnaireActions.setAnswer({ answer }));
        break;
      }
      case 'mark_module': {
        const module = this._utils.convertKeysToCamelCase(message.data);
        this._store.dispatch(
          QuestionnaireActions.markModule({
            module,
            status: {
              qc_mark: module.qcMark,
              sme_mark: module.smeMark,
            },
          }),
        );
        break;
      }
      case 'create_message': {
        const messageBody = {
          ...this._utils.convertKeysToCamelCase(
            JSON.parse(message.data.message),
          ),
          isResolved: false,
        };
        this._store.dispatch(
          SubmissionActions.addToThreadList({
            message: messageBody,
          }),
        );
        this.updateOpenThreads(messageBody);
        this._store.dispatch(
          QuestionnaireActions.updateAnswerThroughSocket({
            answer: messageBody,
          }),
        );
        break;
      }
      case 'reply': {
        const body = {
          ...this._utils.convertKeysToCamelCase(
            JSON.parse(message.data.message),
          ),
          entityId: message.entity_id,
          entityType: message.entity_type,
          isResolved: true,
        };
        this._store.dispatch(
          SubmissionActions.replytoThread({
            message: body,
          }),
        );
        this._store.dispatch(
          QuestionnaireActions.updateAnswerThroughSocket({
            answer: body,
          }),
        );
        break;
      }
      case 'update_answer':
      case 'answer_update': {
        this._store.dispatch(
          QuestionnaireActions.updateAnswerThroughSocket({
            answer: message.data,
          }),
        );
        break;
      }
      default: {
        console.warn('Unknown questionnaire event:', message.event);
        break;
      }
    }
  }

  handleIncomingMessage(message: WebSocketMessage) {
    if (message.event === 'update_answer')
      this._store.dispatch(
        SubmissionActions.answerQuestionnaireThread({
          message: this._utils.convertKeysToCamelCase(JSON.parse(message.data)),
        }),
      );
  }

  handleOutcomeDocumentMessage(message: WebSocketMessage) {
    if (message.event === 'add_outcome_document') {
      /**
       * @TODO: DO A REGRESSION TEST HERE AFTER INTEGRATION
       *
       */
      this._outcomesService.getOutcomes();
    }
  }

  updateOpenThreads(incomingThread: Thread): void {
    this._store
      .select(selectThread)
      .pipe(
        filter((thread): thread is Thread => !!thread),
        take(1),
        tap((thread: Thread) => {
          if (thread.id === incomingThread.id) {
            this._store.dispatch(
              //TODO: UPDATE
              SubmissionActions.storeActiveThread({
                threadId: thread?.id ?? '',
              }),
            );
          }
        }),
      )
      .subscribe();
  }

  handleUserMessage(message: WebSocketMessage) {
    const msgBody =
      typeof message.data === 'string'
        ? JSON.parse(message.data)
        : message.data;
    if (message.event === 'update_resource') {
      let action = null;
      switch (msgBody?.category) {
        case 'document':
          action =
            msgBody?.status?.toLowerCase() !== 'failed'
              ? USER_DOWNLOAD_ACTIONS.downloadSuccess({
                  fileName: msgBody?.file_name ?? '',
                  status: 'success',
                  fileStage: msgBody?.status?.toLowerCase(),
                })
              : USER_DOWNLOAD_ACTIONS.downloadFailure({
                  errorMessage: `Download ${msgBody?.status}`,
                  fileName: msgBody?.file_name ?? '',
                  status: 'failure',
                  fileStage: msgBody?.status?.toLowerCase(),
                });

          break;
        case 'export':
          action =
            msgBody?.status?.toLowerCase() !== 'failed'
              ? USER_EXPORT_ACTIONS.exportSuccess({
                  fileName: msgBody?.file_name ?? '',
                  status: 'success',
                  fileStage: msgBody?.status?.toLowerCase(),
                })
              : USER_EXPORT_ACTIONS.exportFailure({
                  errorMessage: `Export ${msgBody?.status}`,
                  fileName: msgBody?.file_name ?? '',
                  status: 'failure',
                  fileStage: msgBody?.status?.toLowerCase(),
                });

          break;
        default:
          console.warn('Unhandled category:', msgBody?.category);
          break;
      }

      if (action) {
        this._store.dispatch(action);
      }
    }

    if (message.event === 'impersonation') {
      if (msgBody?.impersonation) {
        window.location.href = '/welcome';
      } else {
        const _currentUserId = sessionStorage.getItem('currentUserId');
        if (_currentUserId && _currentUserId.length > 0) {
          const _href = `/profiles/contact-overview/${_currentUserId}/overview`;
          window.location.href = _href;
        } else {
          window.location.href = '/profiles/people/home';
        }
      }
    }
    if (message.event.toLowerCase() === 'user_deactivated') {
      if ('user_deactivation' in msgBody && msgBody.user_deactivation) {
        this._store.dispatch(
          UserActions.onUserDeactivated({
            userId: message.entity_id,
          }),
        );
      }
    }
  }

  // Entry point for handling incoming WebSocket messages for task
  handleTaskInstanceMessage(message: WebSocketMessage) {
    if (
      [
        'create_message',
        'add_document',
        'document',
        'reply',
        'answer_update',
        'update_answer',
        'question_answered',
      ].includes(message.event)
    ) {
      this._reloadTaskListAndCount(message);
      // Update the task detail if it's currently open
      if (message.entity_id) {
        this._taskDetailEventDispatcher.refreshTaskDetail({
          taskDetailId: message.entity_id,
          workspaceId: message?.workspace_id ?? '',
        });
      }
    }
  }

  _reloadTaskListAndCount(message: WebSocketMessage) {
    // Refresh the task list for the relevant workspace
    this._taskEventDispatcher.getTaskList({
      submissionStage: '',
      workspaceID: message?.workspace_id ?? '',
    });
    // Update the count of remaining to-do tasks
    this._taskEventDispatcher.getRemainingToDoTaskCount({
      workspaceID: message?.workspace_id ?? '',
    });
  }

  handleMessage(message: WebSocketMessage) {
    combineLatest([
      this._store.select(selectUser),
      this._store.select(selectSubmission),
    ])
      .pipe(
        take(1),
        map(([user, submission]) => [user as User, submission]),
      )
      .subscribe(([user, submission]) => {
        console.log(user, submission);
        console.log('Received message:', message);
        if (
          !user ||
          typeof user === 'string' ||
          ((!submission?.id ||
            submission?.id !== message.workspace_id ||
            user?.id === message.event_by_user_id) &&
            message.entity_type !== 'user' &&
            message.entity_type !== 'document' &&
            message.entity_type !== 'task_instance' &&
            message.entity_type !== 'questionnaire') // ERM-4796: Added to allow external users to process their own questionnaire messages
        ) {
          return;
        }

        const messageTypeHandlers: {
          [key: string]: (message: WebSocketMessage) => void;
        } = {
          document: (...args) => {
            this.handleDocumentMessage(...args);
            this.handleTaskInstanceMessage(...args);
          },
          message: (...args) => this.handleIncomingMessage(...args),
          outcomeDocument: (...args) =>
            this.handleOutcomeDocumentMessage(...args),
          questionnaire: (...args) => {
            this.handleQuestionnaireMessage(...args);
            this.handleTaskInstanceMessage(...args);
          },
          question_answered: (...args) => {
            this.handleTaskInstanceMessage(...args);
          },
          reviewerDocument: (...args) =>
            this.handleReviewerDocumentMessage(...args),
          // taskInstance for task management
          taskInstance: (...args) => this.handleTaskInstanceMessage(...args),
          user: (...args) => this.handleUserMessage(...args),
        };

        if (message.event === 'question_answered') {
          this.handleTaskInstanceMessage(message);
          return;
        }

        const handler =
          messageTypeHandlers[
            this._utils.convertSnakeToCamelCase(message.entity_type)
          ];

        if (handler) {
          handler(message);
          return;
        }

        console.warn('Received unknown message type:', message.entity_type);
      });
  }
}
